import Email24 from '@honeybook/icons/dist/icons/Email24';
import Tasks24 from '@honeybook/icons/dist/icons/Tasks24';
import Questionnaire24 from '@honeybook/icons/dist/icons/Questionnaire24';
import Brochure24 from '@honeybook/icons/dist/icons/Brochure24';
import FileSparks24 from '@honeybook/icons/dist/icons/FileSparks24';
import FilePlain24 from '@honeybook/icons/dist/icons/FilePlain24';
import { IconProps } from '@honeybook/icons/dist/types/Icon.interface';
import {
    IAutomationTriggerUnitOption,
    TAutomationTriggerType,
    TAutomationTriggerPreposition,
    IAutomationTriggerFilterTypes,
    TAutomationTriggerEventGroup
} from '../types/automation.types';
import {
    AutomationActionFileType,
    AutomationActionType,
    AutomationCombinedTypes
} from '../components/Automations/AutomationsEnums';
import { IVendorEmail } from '../types/model.types';
import { NumberKeys, StringKeys } from '~/types/app.types';
import {
    LabelSpan,
    OptionWrapper
} from '@honeybook/hbui/dist/components/HBDropdown/HBDropdown.styles';
import { newTextStyles } from '@honeybook/hbui/dist/components/HBText/HBText.styles';
import colors from '@honeybook/hbui/dist/styles/colors';
import MoveTo24 from '@honeybook/icons/dist/icons/MoveTo24';
import { IFlowsBetaUserType } from '~/models/user.model';

export interface IAutomationListItem<
    T extends AutomationCombinedTypes = AutomationCombinedTypes
> {
    Icon: (props: IconProps) => JSX.Element;
    value: T;
    title: string;
    description?: string;
}

type TAutomationTypeList<
    T extends AutomationCombinedTypes = AutomationCombinedTypes
> = Record<T, IAutomationListItem<T>>;

export const AUTOMATION_TYPES: TAutomationTypeList<AutomationActionType> = {
    email: {
        Icon: Email24,
        value: 'email',
        title: 'Send Email',
        description: 'Send an email to your leads or clients automatically.'
    },
    task: {
        Icon: Tasks24,
        value: 'task',
        title: 'Create Task',
        description:
            'Write tasks for yourself or your team members so nothing will never be missed throughout a project.'
    },
    file: {
        Icon: FilePlain24,
        value: 'file',
        title: 'Send File via email',
        description:
            'Showcase your services and ask questions using brochures and questionnaires.'
    },
    flow: {
        Icon: FileSparks24,
        value: 'flow',
        title: 'Send Smart File via email',
        description:
            'Enhance your client’s experience with high-converting, branded smart files.'
    },
    pipeline: {
        Icon: MoveTo24,
        value: 'pipeline',
        title: 'Move Pipeline stage',
        description:
            'Automate your workflow by moving your project to a specific pipeline stage.'
    }
};

export const AUTOMATION_FILE_TYPES: TAutomationTypeList<AutomationActionFileType> =
    {
        brochure: {
            Icon: Brochure24,
            value: 'brochure',
            title: 'Brochure',
            description: 'Send Brochure via email'
        },
        questionnaire: {
            Icon: Questionnaire24,
            value: 'questionnaire',
            title: 'Questionnaire',
            description: 'Send Questionnaire via email'
        }
    };

export interface IAutomationTriggerEvent {
    value: TAutomationTriggerType;
    preposition: TAutomationTriggerPreposition;
    isVisible: (userType: IFlowsBetaUserType, order: number) => boolean;
    isDisabled?: boolean;
    filters?: IAutomationTriggerFilterTypes[];
    group: TAutomationTriggerEventGroup;
}

const hasFlows = (userType: IFlowsBetaUserType) => userType.hasFlows;

const hasFiles = (userType: IFlowsBetaUserType) => userType.hasFiles;

export const AUTOMATION_TRIGGER_EVENTS: IAutomationTriggerEvent[] = [
    {
        value: 'before_project_date',
        preposition: 'before',
        isVisible: () => true,
        group: 'project'
    },
    {
        value: 'before_project_end_date',
        preposition: 'before',
        isVisible: () => true,
        group: 'project'
    },
    {
        value: 'after_workflow_execution',
        preposition: 'after',
        isVisible: () => true,
        group: 'project'
    },
    {
        value: 'after_project_date',
        preposition: 'after',
        isVisible: () => true,
        group: 'project'
    },
    {
        value: 'after_project_end_date',
        preposition: 'after',
        isVisible: () => true,
        group: 'project'
    },
    {
        value: 'after_project_move_to_completed',
        preposition: 'after',
        isVisible: () => true,
        group: 'project'
    },
    {
        value: 'after_step_execution',
        preposition: 'after',
        isVisible: (userType, order) => order && order > 0,
        group: 'project'
    },
    {
        value: 'after_contract_signed',
        preposition: 'after',
        isVisible: () => true,
        group: 'file_actions'
    },
    {
        value: 'after_questionnaire_submitted',
        preposition: 'after',
        isVisible: () => true,
        group: 'file_actions'
    },
    {
        value: 'after_first_payment_made',
        preposition: 'after',
        isVisible: () => true,
        group: 'payments'
    },
    {
        value: 'after_invoice_paid_in_full',
        preposition: 'after',
        isVisible: () => true,
        group: 'payments'
    },
    {
        value: 'after_session_scheduled',
        preposition: 'after',
        isVisible: () => true,
        filters: ['session_configs'],
        group: 'scheduler_actions'
    },
    {
        value: 'before_session_start',
        preposition: 'before',
        isVisible: () => true,
        filters: ['session_configs'],
        group: 'scheduler_actions'
    },
    {
        value: 'after_session_start',
        preposition: 'after',
        isVisible: () => true,
        filters: ['session_configs'],
        group: 'scheduler_actions'
    },
    {
        value: 'before_session_end',
        preposition: 'before',
        isVisible: () => true,
        filters: ['session_configs'],
        group: 'scheduler_actions'
    },
    {
        value: 'after_session_end',
        preposition: 'after',
        isVisible: () => true,
        filters: ['session_configs'],
        group: 'scheduler_actions'
    },
    {
        value: 'after_brochure_submitted',
        preposition: 'after',
        isVisible: hasFiles,
        group: 'file_actions'
    },
    {
        value: 'after_flow_completed',
        preposition: 'after',
        isVisible: hasFlows,
        filters: ['templates'],
        group: 'file_actions'
    },
    {
        value: 'after_all_required_signatures_signed',
        preposition: 'after',
        isVisible: hasFlows,
        group: 'file_actions'
    }
];

export const AUTOMATION_TRIGGERS_WITH_FILTERS: TAutomationTriggerType[] = [
    'after_session_scheduled',
    'before_session_start',
    'after_session_start',
    'before_session_end',
    'after_session_end',
    'after_flow_completed'
];

export const AUTOMATION_TRIGGER_DELAY_UNITS: IAutomationTriggerUnitOption[] = [
    {
        value: 'minutes',
        labelPlural: 'minutes',
        labelSingular: 'minute'
    },
    {
        value: 'hours',
        labelPlural: 'hours',
        labelSingular: 'hour'
    },
    {
        value: 'days',
        labelPlural: 'days',
        labelSingular: 'day'
    },
    {
        value: 'weeks',
        labelPlural: 'weeks',
        labelSingular: 'week'
    }
];

export const AUTOMATION_TRIGGER_DELAY_PREPOSITION = [
    { value: 'after', label: 'after' },
    { value: 'before', label: 'before' }
];

export const getAutomationTriggerEvents = (
    userType: IFlowsBetaUserType,
    stepOrder: number
) => {
    return AUTOMATION_TRIGGER_EVENTS.filter(event =>
        event.isVisible(userType, stepOrder)
    );
};

export const AtoZcomparator = <T extends Record<string, any>>(
    a: T,
    b: T,
    key: StringKeys<T> = 'title' as any
) => {
    const [nameA, nameB] = [a, b].map(x => x[key].toUpperCase());

    return comparator(nameA, nameB);
};

export const ISODateStringComparator = <T extends Record<string, any>>(
    a: T,
    b: T,
    key: StringKeys<T> = 'created_at' as any
) => {
    const [timeA, timeB] = [a, b].map(x => Date.parse(x[key]));

    return comparator(timeA, timeB);
};

export const numberComparator = <T extends Record<string, any>>(
    a: T,
    b: T,
    key: NumberKeys<T> = 'import_count' as any
) => {
    const [timeA, timeB] = [a, b].map(x => x[key]);

    return comparator(timeA, timeB);
};

export const comparator = (a, b) => {
    if (a < b) {
        return -1;
    }
    if (a > b) {
        return 1;
    }
    return 0;
};

export const attachmentsCount = (template: IVendorEmail) => {
    return (
        (template.file_attachments?.length || 0) +
        (template.image_attachments?.length || 0)
    );
};

export const INITIAL_STEP_ID = 'initialStep';

export const INVALID_AUTOMATION_ERROR = 'invalid-automation';

export const groupedDropdownCustomStyles = {
    option: {
        borderBottom: 'none',
        paddingInline: '20px 24px',
        [String(OptionWrapper)]: {
            width: '100%',
            maxWidth: '100%'
        },
        [String(LabelSpan)]: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
        }
    },
    groupHeading: {
        ...newTextStyles.subtitle12,
        padding: '12px',
        height: 'auto',
        color: colors.textTertiary
    },
    group: {
        borderTop: `1px solid ${colors.neutral200}`,
        '&:first-child': {
            borderTop: 'none'
        }
    },
    clearIndicator: { display: 'none' },
    menu: {
        width: '100%'
    }
};
